import {
  SCHEDULE_TIME_MAX,
  clampScheduleTimeValue,
} from "./schedule-matrix-time-utils";

const SEGMENT_MAX = {
  hour: SCHEDULE_TIME_MAX.hour,
  minute: SCHEDULE_TIME_MAX.minute,
  second: SCHEDULE_TIME_MAX.second,
};

const SEGMENT_KINDS = ["hour", "minute", "second"];

const proposedSegmentDigits = (element, key) => {
  const value = String(element.value ?? "");
  const start = element.selectionStart ?? value.length;
  const end = element.selectionEnd ?? value.length;
  return `${value.slice(0, start)}${key}${value.slice(end)}`
    .replace(/\D/g, "")
    .slice(0, 2);
};

/** Main Flatpickr text field: strict HH:mm:ss, max 23:59:59 (local DOM only). */
export const sanitizeFlatpickrMainTimeInput = (input) => {
  if (!input) return;
  let value = String(input.value ?? "");
  value = value.replace(/[^\d:]/g, "");

  const parts = value.split(":");

  if (parts[0] !== undefined && parts[0].length > 2) {
    parts[0] = parts[0].slice(0, 2);
  }
  if (parts[1] !== undefined && parts[1].length > 2) {
    parts[1] = parts[1].slice(0, 2);
  }
  if (parts[2] !== undefined && parts[2].length > 2) {
    parts[2] = parts[2].slice(0, 2);
  }

  if (parts[0]?.length === 2 && Number(parts[0]) > SCHEDULE_TIME_MAX.hour) {
    parts[0] = String(SCHEDULE_TIME_MAX.hour).padStart(2, "0");
  }
  if (parts[1]?.length === 2 && Number(parts[1]) > SCHEDULE_TIME_MAX.minute) {
    parts[1] = String(SCHEDULE_TIME_MAX.minute).padStart(2, "0");
  }
  if (parts[2]?.length === 2 && Number(parts[2]) > SCHEDULE_TIME_MAX.second) {
    parts[2] = String(SCHEDULE_TIME_MAX.second).padStart(2, "0");
  }

  if (parts[0] && Number(parts[0]) > SCHEDULE_TIME_MAX.hour) {
    parts[0] = String(SCHEDULE_TIME_MAX.hour);
  }
  if (parts[1] && Number(parts[1]) > SCHEDULE_TIME_MAX.minute) {
    parts[1] = String(SCHEDULE_TIME_MAX.minute);
  }
  if (parts[2] && Number(parts[2]) > SCHEDULE_TIME_MAX.second) {
    parts[2] = String(SCHEDULE_TIME_MAX.second);
  }

  const joined = parts.join(":");
  const clamped = clampScheduleTimeValue(joined);
  if (clamped && clamped.includes(":")) {
    input.value = clamped;
  } else if (clamped && /^\d{1,2}$/.test(joined.trim())) {
    input.value = clamped;
  } else {
    input.value = joined;
  }
};

const coerceSegmentElement = (input, max, { pad = false } = {}) => {
  const digits = String(input.value ?? "").replace(/\D/g, "").slice(0, 2);
  if (!digits) {
    input.value = "";
    return false;
  }
  let n = Number.parseInt(digits, 10);
  if (!Number.isFinite(n)) n = 0;
  const hitMax = n > max;
  if (hitMax) n = max;
  if (pad || digits.length >= 2) {
    input.value = String(n).padStart(2, "0");
  } else {
    input.value = String(n);
  }
  return hitMax;
};

const padSegmentElements = (instance) => {
  const elements = [
    [instance.hourElement, SEGMENT_MAX.hour],
    [instance.minuteElement, SEGMENT_MAX.minute],
    [instance.secondElement, SEGMENT_MAX.second],
  ];
  elements.forEach(([el, max]) => {
    if (!el) return;
    coerceSegmentElement(el, max, { pad: true });
  });
};

const boundSegmentInputs = new WeakSet();
const boundMainInputs = new WeakSet();
const boundTimeContainers = new WeakSet();

/** Main field: display only; open picker on click, edit in popup segments. */
export const lockFlatpickrMainTimeInput = (main) => {
  if (!main) return;
  main.readOnly = true;
  main.setAttribute("readonly", "readonly");
  main.setAttribute("aria-readonly", "true");
  main.setAttribute("tabindex", "0");
  main.classList.add("schedule-matrix-time-input--picker-only");
};

/** Read popup segments or main field into clamped HH:mm:ss. */
export const readScheduleTimeFromFlatpickrInstance = (instance) => {
  if (!instance) return "";
  const h = String(instance.hourElement?.value ?? "").trim();
  const m = String(instance.minuteElement?.value ?? "").trim();
  const s = String(instance.secondElement?.value ?? "").trim();
  if (h !== "" || m !== "" || s !== "") {
    const fromSegments = clampScheduleTimeValue(
      `${h || "00"}:${m || "00"}:${s || "00"}`
    );
    if (fromSegments) return fromSegments;
  }
  const raw = String(instance.input?.value ?? instance._input?.value ?? "").trim();
  return clampScheduleTimeValue(raw) || "";
};

const writeTimeToMainInput = (instance, timeStr) => {
  const main = instance.input || instance._input;
  if (main) {
    main.value = timeStr;
  }
};

/** Push picker segments to the main time field (live while typing). */
export const syncPickerTimeToMainField = (
  instance,
  onSynced,
  { finalize = false, notifyParent = true, updateFlatpickrDate = false } = {}
) => {
  if (!instance) return;
  if (finalize) {
    padSegmentElements(instance);
  }
  const clamped = readScheduleTimeFromFlatpickrInstance(instance);
  const notify = notifyParent ? instance.__scheduleTimeOnSynced || onSynced : null;
  if (!clamped) {
    writeTimeToMainInput(instance, "");
    notify?.("");
    return;
  }
  if (finalize || updateFlatpickrDate) {
    try {
      instance.setDate(clamped, false, "H:i:S");
    } catch (_err) {
      /* keep writing the input even if parse fails */
    }
  }
  writeTimeToMainInput(instance, clamped);
  notify?.(clamped);
};

/** Commit clamped time to main field + Flatpickr state + parent callback. */
export const commitFlatpickrScheduleTime = (instance, onSynced) => {
  syncPickerTimeToMainField(instance, onSynced, {
    finalize: true,
    notifyParent: true,
  });
};

const bindSegmentInput = (input, kind, instance, onSynced) => {
  if (!input || boundSegmentInputs.has(input)) return;
  boundSegmentInputs.add(input);
  const max = SEGMENT_MAX[kind];

  if (input.type === "number") {
    input.type = "text";
    input.setAttribute("inputmode", "numeric");
  }
  input.setAttribute("maxlength", "2");
  input.removeAttribute("readonly");

  input.addEventListener(
    "keydown",
    (event) => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (event.key.length === 1 && /\d/.test(event.key)) {
        const nextDigits = proposedSegmentDigits(input, event.key);
        if (nextDigits.length > 2) {
          event.preventDefault();
          return;
        }
        const proposed = Number.parseInt(nextDigits, 10);
        if (Number.isFinite(proposed) && proposed > max) {
          event.preventDefault();
          input.value = String(max).padStart(2, "0");
          syncPickerTimeToMainField(instance, onSynced, {
            finalize: false,
            notifyParent: false,
            updateFlatpickrDate: true,
          });
        }
      }
    },
    true
  );

  input.addEventListener("input", () => {
    const hitMax = coerceSegmentElement(input, max, { pad: false });
    syncPickerTimeToMainField(instance, onSynced, {
      finalize: false,
      notifyParent: false,
      updateFlatpickrDate: hitMax,
    });
  });

  input.addEventListener("blur", () => {
    coerceSegmentElement(input, max, { pad: true });
    commitFlatpickrScheduleTime(instance, onSynced);
  });
};

const bindTimeContainerBlur = (instance, onSynced) => {
  const container = instance.timeContainer;
  if (!container || boundTimeContainers.has(container)) return;
  boundTimeContainers.add(container);

  container.addEventListener(
    "focusout",
    (event) => {
      const next = event.relatedTarget;
      if (next && container.contains(next)) return;
      commitFlatpickrScheduleTime(instance, onSynced);
    },
    true
  );
};

export const attachFlatpickrScheduleTimeGuards = (instance, onSynced) => {
  if (!instance) return;
  instance.__scheduleTimeOnSynced = onSynced;

  const main = instance.input;
  if (main && !boundMainInputs.has(main)) {
    boundMainInputs.add(main);
    lockFlatpickrMainTimeInput(main);

    main.addEventListener(
      "keydown",
      (event) => {
        if (event.key === "Tab" || event.key === "Escape" || event.key === "Enter") {
          return;
        }
        event.preventDefault();
      },
      true
    );
    main.addEventListener("blur", () => {
      commitFlatpickrScheduleTime(instance, onSynced);
    });
  } else if (main) {
    lockFlatpickrMainTimeInput(main);
  }

  SEGMENT_KINDS.forEach((kind) => {
    const el =
      kind === "hour"
        ? instance.hourElement
        : kind === "minute"
          ? instance.minuteElement
          : instance.secondElement;
    bindSegmentInput(el, kind, instance, onSynced);
  });

  bindTimeContainerBlur(instance, onSynced);
};

export const SCHEDULE_MATRIX_FLATPICKR_OPTIONS = {
  enableTime: true,
  noCalendar: true,
  enableSeconds: true,
  dateFormat: "H:i:S",
  time_24hr: true,
  defaultHour: 0,
  defaultMinute: 0,
  defaultSeconds: 0,
  minTime: "00:00:00",
  maxTime: "23:59:59",
  allowInput: false,
  disableMobile: true,
  clickOpens: true,
};
